import {
  CohortBuilderDefinitionLegacy,
  CohortDslLegacy,
  SimpleEventLegacy,
} from '@verantos-dev/types-cohorts';
import {
  getFullDslQueryString,
  transformDerivedEventsToDsl,
  transformSimpleEventToDsl,
  transformSubjectVariablesToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

import { getNextDerivedEventsForSimpleEventNameChange } from '../getNextDerivedEventsForSimpleEventNameChange';
import { getNextSubjectVariablesForSimpleEventNameChange } from '../getNextSubjectVariablesForSimpleEventNameChange';

export const getFullCohortDslForSimpleEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextSimpleEvent: SimpleEventLegacy
) => {
  const currentSimpleEvents = currentCohortDefinition?.simpleEvents ?? [];
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
    (simpleEvent) => simpleEvent.id === nextSimpleEvent.id
  );

  // check if the name of the simple event has changed, to determine if we need to update the usages of this name
  // in items that reference this simple event.
  const isSimpleEventNameChanged =
    payloadSimpleEventIndex > -1 &&
    currentSimpleEvents[payloadSimpleEventIndex].name !== nextSimpleEvent.name;

  const nextDerivedEvents = isSimpleEventNameChanged
    ? getNextDerivedEventsForSimpleEventNameChange(
        currentDerivedEvents,
        currentSimpleEvents[payloadSimpleEventIndex].name,
        nextSimpleEvent.name,
        nextSimpleEvent.type
      )
    : currentDerivedEvents;
  const nextSubjectVariables = isSimpleEventNameChanged
    ? getNextSubjectVariablesForSimpleEventNameChange(
        currentSubjectVariables,
        currentSimpleEvents[payloadSimpleEventIndex].name,
        nextSimpleEvent.name,
        nextSimpleEvent.type
      )
    : currentSubjectVariables;

  // If the simple events exists in state, replace simple event dsl string
  // otherwise, add the new simple event dsl string to the end of the array
  const newSimpleEventDSL = transformSimpleEventToDsl(nextSimpleEvent);
  const simpleEventStrings = currentCohortDefinition?.cohortDsl.simpleEvents ?? [];
  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria: currentCohortDefinition?.cohortDsl.inclusionCriteria ?? [],
    exclusionCriteria: currentCohortDefinition?.cohortDsl.exclusionCriteria ?? [],
    studyPeriodRestrictions: currentCohortDefinition?.cohortDsl.studyPeriodRestrictions ?? [],
    subjectVariables: transformSubjectVariablesToDsl(nextSubjectVariables),
    indexDate: currentCohortDefinition?.cohortDsl.indexDate ?? undefined,
    derivedEvents: transformDerivedEventsToDsl(nextDerivedEvents),
    simpleEvents:
      payloadSimpleEventIndex > -1
        ? simpleEventStrings.map((simpleEventString, i) => {
            if (i === payloadSimpleEventIndex) {
              return newSimpleEventDSL;
            }
            return simpleEventString;
          })
        : [...simpleEventStrings, newSimpleEventDSL],
  };
  return getFullDslQueryString(newCohortDsl);
};

export const getFullCohortDslForDeleteSimpleEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  simpleEventId: string
) => {
  const currentSimpleEvents = currentCohortDefinition?.simpleEvents ?? [];
  const currentCohortDsl = currentCohortDefinition?.cohortDsl;
  const currentSimpleEventsDsl = currentCohortDsl?.simpleEvents ?? [];
  const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
    (simpleEvent) => simpleEvent.id === simpleEventId
  );

  const newCohortDsl: CohortDslLegacy = {
    inclusionCriteria: currentCohortDsl?.inclusionCriteria ?? [],
    exclusionCriteria: currentCohortDsl?.exclusionCriteria ?? [],
    studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
    indexDate: currentCohortDsl?.indexDate,
    subjectVariables: currentCohortDsl?.subjectVariables ?? [],
    derivedEvents: currentCohortDsl?.derivedEvents ?? [],
    simpleEvents: [
      ...currentSimpleEventsDsl.slice(0, payloadSimpleEventIndex),
      ...currentSimpleEventsDsl.slice(payloadSimpleEventIndex + 1),
    ],
  };
  return { newCohortDsl, fullDslQueryString: getFullDslQueryString(newCohortDsl) };
};
